(() => {
  const initGallery = (root) => {
    if (root.dataset.nhMediaLoaderReady === 'true') return;
    root.dataset.nhMediaLoaderReady = 'true';

    const track = root.querySelector('[data-gallery-scroll]');
    const slides = [...root.querySelectorAll('[data-slide-index]')];
    const thumbs = [...root.querySelectorAll('[data-thumb-index]')];
    const prev = root.querySelector('[data-gallery-prev]');
    const next = root.querySelector('[data-gallery-next]');
    const slideLoads = new Map();
    const renderedSlideLoads = new Map();
    const activeIndex = () => Math.round((track?.scrollLeft || 0) / Math.max(1, track?.clientWidth || 1));

    const hydrateThumb = (index) => {
      const image = thumbs[index]?.querySelector('img[data-thumb-src]');
      if (!image || image.dataset.thumbLoading === 'true') return;

      if (index === 0) {
        const primary = slides[0]?.querySelector('img');
        const source = primary?.naturalWidth > 0 ? (primary.currentSrc || primary.src) : '';
        if (source) {
          image.src = source;
          image.classList.add('is-loaded');
          image.removeAttribute('data-thumb-src');
          return;
        }
        const waits = Number(image.dataset.thumbPrimaryWait || 0);
        if (waits < 8) {
          image.dataset.thumbPrimaryWait = String(waits + 1);
          window.setTimeout(() => hydrateThumb(0), 600);
        }
        return;
      }

      const attempt = Number(image.dataset.thumbAttempt || 0);
      if (attempt >= 3) return;
      const source = image.dataset.thumbSrc;
      if (!source) return;
      image.dataset.thumbLoading = 'true';
      image.dataset.thumbAttempt = String(attempt + 1);
      const loader = new Image();
      loader.decoding = 'async';
      loader.onload = () => {
        image.src = source;
        image.classList.add('is-loaded');
        image.removeAttribute('data-thumb-src');
        delete image.dataset.thumbLoading;
      };
      loader.onerror = () => {
        delete image.dataset.thumbLoading;
        window.setTimeout(() => hydrateThumb(index), 900 + (attempt * 500));
      };
      loader.src = source;
    };

    const hydratePrimary = () => {
      const image = root.querySelector('img[data-gallery-primary]');
      if (!image || image.dataset.galleryPrimaryLoading === 'true' || image.naturalWidth > 0) return;
      const attempt = Number(image.dataset.galleryPrimaryAttempt || 0);
      if (attempt >= 2) return;
      const source = attempt === 0 ? image.dataset.galleryFallbackSrc : image.dataset.galleryPreviewSrc;
      if (!source) return;
      image.dataset.galleryPrimaryLoading = 'true';
      image.dataset.galleryPrimaryAttempt = String(attempt + 1);
      const loader = new Image();
      loader.decoding = 'async';
      loader.onload = () => {
        image.removeAttribute('srcset');
        image.src = source;
        delete image.dataset.galleryPrimaryLoading;
        hydrateThumb(0);
      };
      loader.onerror = () => {
        delete image.dataset.galleryPrimaryLoading;
        window.setTimeout(hydratePrimary, 900 + (attempt * 500));
      };
      loader.src = source;
    };

    const hydrateSlide = (index) => {
      const image = slides[index]?.querySelector('img[data-gallery-src]');
      if (!image) return Promise.resolve(null);
      if (slideLoads.has(index)) return slideLoads.get(index);
      const attempt = Number(image.dataset.galleryAttempt || 0);
      if (attempt >= 3) return Promise.resolve(null);
      const source = attempt === 0 ? image.dataset.gallerySrc : (attempt === 1 ? image.dataset.galleryFallbackSrc : image.dataset.galleryPreviewSrc);
      if (!source) return Promise.resolve(null);

      const preview = image.dataset.galleryPreviewSrc;
      if (preview && image.src.startsWith('data:image/')) {
        image.src = preview;
        image.classList.add('is-preview');
      }

      image.dataset.galleryLoading = 'true';
      image.dataset.galleryAttempt = String(attempt + 1);
      const loader = new Image();
      loader.decoding = 'async';
      if (attempt === 0 && image.dataset.gallerySrcset) {
        loader.srcset = image.dataset.gallerySrcset;
        loader.sizes = image.dataset.gallerySizes || '100vw';
      }

      const promise = new Promise((resolve) => {
        loader.onload = async () => {
          try {
            if (typeof loader.decode === 'function') await loader.decode();
          } catch (_) {
            // A successful load is enough when decode() is unavailable or interrupted.
          }

          image.src = source;
          if (attempt === 0 && image.dataset.gallerySrcset) {
            image.srcset = image.dataset.gallerySrcset;
            image.sizes = image.dataset.gallerySizes || '100vw';
          }
          image.fetchPriority = index === activeIndex() ? 'high' : 'auto';
          image.classList.remove('is-preview');
          image.classList.add('is-loaded');
          image.removeAttribute('data-gallery-src');
          image.removeAttribute('data-gallery-fallback-src');
          image.removeAttribute('data-gallery-preview-src');
          image.removeAttribute('data-gallery-srcset');
          image.removeAttribute('data-gallery-sizes');
          delete image.dataset.galleryLoading;
          slideLoads.delete(index);
          resolve(image);
        };
        loader.onerror = () => {
          delete image.dataset.galleryLoading;
          slideLoads.delete(index);
          window.setTimeout(() => hydrateSlide(index), 900 + (attempt * 500));
          resolve(null);
        };
        loader.src = source;
      });

      slideLoads.set(index, promise);
      return promise;
    };

    const preloadRenderedSlide = (index) => {
      const image = slides[index]?.querySelector('img:not([data-gallery-src])');
      if (!image) return Promise.resolve(null);
      if (renderedSlideLoads.has(index)) return renderedSlideLoads.get(index);

      image.loading = 'eager';
      image.fetchPriority = index === activeIndex() ? 'high' : 'auto';
      if (image.complete && image.naturalWidth > 0) {
        return typeof image.decode === 'function' ? image.decode().catch(() => null) : Promise.resolve(image);
      }

      const source = image.currentSrc || image.src;
      if (!source) return Promise.resolve(null);
      const loader = new Image();
      loader.decoding = 'async';
      const srcset = image.getAttribute('srcset');
      const sizes = image.getAttribute('sizes');
      if (srcset) loader.srcset = srcset;
      if (sizes) loader.sizes = sizes;

      const promise = new Promise((resolve) => {
        loader.onload = async () => {
          try {
            if (typeof loader.decode === 'function') await loader.decode();
            if (typeof image.decode === 'function') await image.decode();
          } catch (_) {
            // The decoded loader has already warmed the browser cache.
          }
          renderedSlideLoads.delete(index);
          resolve(image);
        };
        loader.onerror = () => {
          renderedSlideLoads.delete(index);
          resolve(null);
        };
        loader.src = source;
      });

      renderedSlideLoads.set(index, promise);
      return promise;
    };

    const primeSlidesAround = (index) => {
      const current = Math.max(0, Math.min(slides.length - 1, index));
      hydrateSlide(current);
      hydrateSlide(current + 1);
      hydrateSlide(current - 1);
      preloadRenderedSlide(current);
      preloadRenderedSlide(current + 1);
      preloadRenderedSlide(current - 1);
      window.setTimeout(() => {
        hydrateSlide(current + 2);
        preloadRenderedSlide(current + 2);
      }, 220);
    };

    thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => {
      hydrateThumb(index);
      primeSlidesAround(index);
    }));
    const primePrevious = () => primeSlidesAround(Math.max(0, activeIndex() - 1));
    const primeNext = () => primeSlidesAround(Math.min(slides.length - 1, activeIndex() + 1));
    prev?.addEventListener('pointerenter', primePrevious, { passive: true });
    prev?.addEventListener('pointerdown', primePrevious, { passive: true });
    prev?.addEventListener('click', primePrevious);
    next?.addEventListener('pointerenter', primeNext, { passive: true });
    next?.addEventListener('pointerdown', primeNext, { passive: true });
    next?.addEventListener('click', primeNext);
    track?.addEventListener('scroll', () => window.requestAnimationFrame(() => primeSlidesAround(activeIndex())), { passive: true });

    const primary = root.querySelector('img[data-gallery-primary]') || slides[0]?.querySelector('img');
    if (primary) {
      primary.addEventListener('error', hydratePrimary);
      if (primary.complete && primary.naturalWidth === 0) hydratePrimary();
      window.setTimeout(hydratePrimary, 1800);

      const primeFollowingSlides = () => {
        hydrateSlide(1);
        window.setTimeout(() => hydrateSlide(2), 260);
      };
      if (primary.complete && primary.naturalWidth > 0) {
        primeFollowingSlides();
      } else {
        primary.addEventListener('load', primeFollowingSlides, { once: true });
        window.setTimeout(primeFollowingSlides, 1400);
      }
    }
    thumbs.forEach((thumb, index) => window.setTimeout(() => hydrateThumb(index), 700 + (index * 280)));
  };

  const init = () => {
    const galleries = new Set();
    document.querySelectorAll('[data-gallery-scroll]').forEach((track) => {
      galleries.add(track.closest('.nh-cat__gallery, .nh-pdp__gallery') || track.parentElement);
    });
    galleries.forEach((gallery) => gallery && initGallery(gallery));
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
